import auth from 'frontcore/auth'
import { getLanguage } from 'frontcore/i18n'
import { I18N_URL } from 'frontcore/constants/communications'
import { WEB_URL_LINK_REGEX } from 'frontcore/constants/constants'


let httpLibrary

export function injectHttpLibrary (library) {
  httpLibrary = library
}

/**
 * @param { string } url - URL string
 * @returns { Boolean }
 */
export function requestNeedHeaders (url) {
  const urlObject = new URL(url)
  return ![`/${I18N_URL}/`].includes(urlObject.pathname)
}

function getHttpLibrary () {
  if (httpLibrary === undefined) {
    throw new Error('An httpLibrary needs to be set, you fool!')
  }
  return httpLibrary
}

export function save (url, data, isNew) {
  return _save(url, data, isNew)
}

export function patch (url, data) {
  return _patch(url, data)
}

export function deleteObject (url) {
  return _delete(url)
}

/**
 * @template T
 * @param { string } url - URL string
 * @param { Record<string, unknown> | Record<string, unknown>[] } data - Request body
 * @returns { Promise<T> }
 */
export function _post (url, data) {
  return _save(url, data, true)
}

/**
 * @param { string } url - URL string
 * @returns { Promise<unknown> }
 */
export function get (url) {
  return _get(url, getHeaders())
}

/**
 * @param { string } url - URL string
 * @param { Record<string, string> | undefined } [headers=undefined] - Header configuration
 * @returns { Promise<unknown> }
 */
export function _get (url, headers) {
  return getHttpLibrary().get(url, true, true, true, headers)
    .then(extractBodyResponse)
}

export function _save (url, data, isNew) {
  const restVerb = isNew ? 'post' : 'put'
  return getHttpLibrary()[restVerb](url, data, getHeaders())
    .then(extractBodyResponse)
}

export function _patch (url, data) {
  return getHttpLibrary().patch(url, data, getHeaders())
    .then(extractBodyResponse)
}

export function _postFormData (url, data) {
  const headers = getHeaders()
  const newHeaders = {
    headers: Object.assign(headers.headers, {
      'Content-Type': 'multipart/form-data',
    }),
    showLoading: headers.showLoading,
  }
  return getHttpLibrary().post(url, data, newHeaders)
    .then(extractBodyResponse)
}

export function _delete (url, data = null) {
  return getHttpLibrary().delete(url, data)
    .then((extractBodyResponse))
}

export function _head (url) {
  const config = getHeaders()
  return getHttpLibrary().head(url, config)
    .then((extractBodyResponse))
}

/**
 * @param {{ token: string, includeLanguage?: boolean }} - Valid Token
 * @returns {headers: Record<string, string>}
 */
export function getHeadersFromToken (token, includeLanguage = true) {
  const headers = {}
  if (includeLanguage ?? true) {
    headers['Accept-Language'] = getLanguage()
  }
  headers['Authorization'] = `Token ${token}`
  return headers
}

/**
 * @param {{ includeLanguage?: boolean, includeToken?: boolean, showLoading?: boolean }} - Header configuration
 * @returns {{ headers: Record<string, string>, showLoading: boolean }}
 */
export function getHeaders ({
  includeLanguage,
  includeToken,
  showLoading,
} = {}) {
  const headers = {}
  if (includeLanguage ?? true) {
    headers['Accept-Language'] = getLanguage()
  }
  if (includeToken ?? true) {
    headers['Authorization'] = auth.getAuthHeader()
  }
  headers['Accept-Version'] = API_VERSION
  return {
    headers: headers,
    showLoading: showLoading ?? false,
  }
}

/**
 *
 * @param parameters {Record<string, unknown> | null}
 * @param strict {boolean}
 * @return {string|null}
 */
export function getUrlQueryParameterFromObject (parameters, strict = true) {
  if (typeof parameters !== 'object' || parameters === null) {
    return null
  }
  return Object.keys(parameters)
    .filter(key => strict || (parameters[key] !== null && parameters[key] !== undefined))
    .map(key => {
      let value = parameters[key]
      if (typeof value === 'boolean') { value = value ? 'True' : 'False' }
      if (Array.isArray(value)) { value = value.join(',') }
      return `${key}=${value}`
    }).join('&')
}

export function isValidHttpUrl (string) {
  return WEB_URL_LINK_REGEX.test(string)
}

/**
 * @param { Promise<unknown> } response - API response
 * @returns { Record<string, string><unknown> }
 */
export function extractBodyResponse (response) {
  try {
    return 'body' in response ? response.body : response
  } catch {
    return response
  }
}
